import type { MetricFnInterface, MetricOptions } from '@zg-rentals/monitor-base';
import type { Logger } from '@zg-rentals/logger-browser';
import { MonitorReporter } from '@zg-rentals/monitor-base';
import type { MarlinOptions } from '@zg-rentals/marlin-browser';
import { MarlinBrowser } from '@zg-rentals/marlin-browser';

export class BrowserMonitorPontoonReporter extends MonitorReporter {
  marlin: MarlinBrowser;
  proxyPath?: string;
  extraMetricOptions: { serviceName?: string; serverName?: 'browser' };

  constructor({
    logger,
    proxyPath,
    sampleRate,
    marlinOpts = {},
  }: {
    logger?: Logger;
    proxyPath?: string;
    marlinOpts?: MarlinOptions;
    sampleRate?: number;
  }) {
    super({
      reporterName: 'BrowserPontoonReporter',
      logger,
      sampleRate,
    });

    if (marlinOpts?.useCrossOrigin) {
      if (!marlinOpts.appName) {
        throw new Error(
          'Cannot instantiate BrowserMonitorPontoonReporter without the appName option when not using a proxy.',
        );
      }
      this.extraMetricOptions = { serviceName: marlinOpts.appName, serverName: 'browser' };
    } else {
      if (proxyPath === undefined) {
        throw new Error('Cannot instantiate BrowserMonitorPontoonReporter without the proxyPath option.');
      }
      if (proxyPath[0] !== '/') {
        proxyPath = `/${proxyPath}`;
      }
      if (proxyPath[proxyPath.length - 1] === '/') {
        proxyPath = proxyPath.substring(0, proxyPath.length - 1);
      }

      this.extraMetricOptions = {};
      this.proxyPath = proxyPath;
    }

    this.logger = logger;
    this.marlin = new MarlinBrowser({
      // if not using proxy, `api` will be set in Marlin constructor to appropriate URL using `getEnvironmentUrl`
      ...(!marlinOpts?.useCrossOrigin && { api: window.location.origin + this.proxyPath }),
      ...marlinOpts,
    });
  }

  onInitialize(logger?: Logger) {
    this.logger = this.logger || logger;
  }

  shouldSave({ sampleRate }: MetricOptions = {}) {
    const random = Math.random();
    return random <= this.sampleRate && (typeof sampleRate !== 'number' || random <= sampleRate);
  }

  async count({ name, amount = 1, options }: MetricFnInterface) {
    if (this.shouldSave(options)) {
      await this.marlin
        .sendCounts({
          ...this.extraMetricOptions,
          countStats: {
            [Date.now()]: {
              [name]: { sum: amount },
            },
          },
        })
        .catch((e) => this.onError({ error: e }));
    }
  }

  async gauges({ name, amount, options }: MetricFnInterface): Promise<void> {
    if (this.shouldSave(options)) {
      await this.marlin
        .sendGauges({
          ...this.extraMetricOptions,
          gauges: {
            [Date.now()]: {
              [name]: {
                count: 1,
                sum: amount,
                min: amount,
                max: amount,
              },
            },
          },
        })
        .catch((e) => this.onError({ error: e }));
    }
  }

  async measurementWithGauges({ name, amount, options }: MetricFnInterface): Promise<void> {
    if (this.shouldSave(options)) {
      await this.marlin
        .sendMeasurementWithGauges({
          ...this.extraMetricOptions,
          measurements: {
            [name]: [{ timeMs: Date.now(), value: amount }],
          },
        })
        .catch((e) => this.onError({ error: e }));
    }
  }

  async onError({ error }: { error: Error }) {
    this.logger?.error(error);
  }
}
